import * as React from 'react';
import { useEffect } from 'react';
import { PageSection } from '@patternfly/react-core';
import { WrenchIcon, ChartLineIcon, CogIcon } from "@patternfly/react-icons";
import { getOrganizations } from "@app/utils/utils";
import { useNavigate } from "react-router-dom";

const Analytics: React.FunctionComponent<any> = (props) => {
    const navigate = useNavigate();

    useEffect(() => {
        // Check if user has organizations - redirect if not
        getOrganizations().then(function(result) {
            if (result == undefined || result == null || !result.organizations || result.organizations.length == 0) {
                navigate('/organizations');
            }
        });
    }, []);

    const styles = {
        container: {
            display: 'flex',
            flexDirection: 'column' as const,
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 'calc(100vh - 200px)',
            padding: '40px 24px'
        },
        banner: {
            background: '#ffffff',
            borderRadius: '24px',
            padding: '48px',
            textAlign: 'center' as const,
            maxWidth: '600px',
            width: '100%',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1)',
            position: 'relative' as const,
            overflow: 'hidden'
        },
        decorationTop: {
            position: 'absolute' as const,
            top: 0,
            left: 0,
            right: 0,
            height: '6px',
            background: 'linear-gradient(90deg, #667eea 0%, #764ba2 50%, #f093fb 100%)'
        },
        iconContainer: {
            position: 'relative' as const,
            display: 'inline-block',
            marginBottom: '24px'
        },
        mainIcon: {
            width: '100px',
            height: '100px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#ffffff',
            fontSize: '44px',
            margin: '0 auto',
            animation: 'pulse 2s ease-in-out infinite'
        },
        floatingIcon1: {
            position: 'absolute' as const,
            top: '-10px',
            right: '-20px',
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#ffffff',
            fontSize: '18px',
            boxShadow: '0 4px 14px rgba(240, 147, 251, 0.4)'
        },
        floatingIcon2: {
            position: 'absolute' as const,
            bottom: '0px',
            left: '-15px',
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#ffffff',
            fontSize: '16px',
            boxShadow: '0 4px 14px rgba(79, 172, 254, 0.4)'
        },
        title: {
            fontSize: '28px',
            fontWeight: 700,
            color: '#1f2937',
            margin: '0 0 12px 0'
        },
        subtitle: {
            fontSize: '18px',
            color: '#6b7280',
            margin: '0 0 24px 0',
            lineHeight: 1.6
        },
        progressContainer: {
            background: '#f3f4f6',
            borderRadius: '100px',
            height: '12px',
            width: '100%',
            maxWidth: '300px',
            margin: '0 auto 16px',
            overflow: 'hidden'
        },
        progressBar: {
            height: '100%',
            width: '65%',
            background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '100px',
            animation: 'shimmer 2s ease-in-out infinite'
        },
        progressText: {
            fontSize: '14px',
            color: '#9ca3af',
            margin: '0 0 32px 0'
        },
        featureList: {
            display: 'flex',
            flexWrap: 'wrap' as const,
            justifyContent: 'center',
            gap: '12px'
        },
        featureTag: {
            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
            color: '#667eea',
            padding: '8px 16px',
            borderRadius: '100px',
            fontSize: '13px',
            fontWeight: 500
        },
        hardHat: {
            fontSize: '48px',
            marginBottom: '8px'
        }
    };

    const keyframes = `
        @keyframes pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.05); }
        }
        @keyframes shimmer {
            0% { opacity: 1; }
            50% { opacity: 0.7; }
            100% { opacity: 1; }
        }
    `;

    return (
        <PageSection>
            <style>{keyframes}</style>
            <div style={styles.container}>
                <div style={styles.banner}>
                    <div style={styles.decorationTop}></div>

                    <div style={styles.hardHat}>🏗️</div>

                    <div style={styles.iconContainer}>
                        <div style={styles.mainIcon}>
                            <ChartLineIcon />
                        </div>
                        <div style={styles.floatingIcon1}>
                            <WrenchIcon />
                        </div>
                        <div style={styles.floatingIcon2}>
                            <CogIcon />
                        </div>
                    </div>

                    <h1 style={styles.title}>Analytics Coming Soon!</h1>
                    <p style={styles.subtitle}>
                        We're building something amazing to help you track your ad performance and gain valuable insights.
                    </p>

                    <div style={styles.progressContainer}>
                        <div style={styles.progressBar}></div>
                    </div>
                    <p style={styles.progressText}>65% complete - Hang tight!</p>

                    <div style={styles.featureList}>
                        <span style={styles.featureTag}>Campaign Metrics</span>
                        <span style={styles.featureTag}>Audience Insights</span>
                        <span style={styles.featureTag}>ROI Tracking</span>
                        <span style={styles.featureTag}>Custom Reports</span>
                    </div>
                </div>
            </div>
        </PageSection>
    );
}

export { Analytics };
